import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { User } from "@/types/auth";
import { analytics } from "@/lib/analytics";
import { rsAnalytics } from "@/lib/rudderstack";
import * as Sentry from "@sentry/react";
import { clearHubspotChatIdentity } from "@/lib/hubspotChatReset";

export function useAuth() {
  const queryClient = useQueryClient();
  
  // Check if we're on an auth page to avoid unnecessary API calls
  const isOnAuthPage = window.location.pathname.startsWith('/auth/');
  
  // Use state to track token presence so it can be reactively updated
  const [hasToken, setHasToken] = useState(() => !!localStorage.getItem('token'));
  
  // Re-check token on mount and page visibility change to handle logout from other tabs/bfcache
  useEffect(() => {
    const checkToken = () => {
      const tokenExists = !!localStorage.getItem('token');
      setHasToken(tokenExists);
      
      // If token is gone but we have cached auth data, clear it
      if (!tokenExists) {
        queryClient.removeQueries({ queryKey: ['/api/auth/user'] });
        queryClient.removeQueries({ queryKey: ['/api/dashboard'] });
        queryClient.removeQueries({ queryKey: ['/api/billing'] });
      }
    };
    
    // Check on mount
    checkToken();
    
    // Check when page becomes visible (handles bfcache and tab switching)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkToken();
      }
    };
    
    // Check on focus (handles switching back to the tab)
    const handleFocus = () => {
      checkToken();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [queryClient]);
  
  // Fetch user data using proper JWT authentication
  // Don't fetch if on auth pages to prevent interference
  const { data: authResponse, isLoading, error } = useQuery<{user: User, organization: any}>({
    queryKey: ["/api/auth/user"],
    queryFn: () => apiRequest('GET', '/api/auth/user'),
    retry: false,
    refetchInterval: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: hasToken && !isOnAuthPage, // Only fetch if we have a token AND not on auth pages
  });

  // Extract user from API response
  const user = authResponse?.user;

  const logout = () => {
    rsAnalytics.track('user_logged_out');
    rsAnalytics.reset();
    Sentry.setUser(null);
    // The anonymous order-page chat must not reopen in this user's thread.
    clearHubspotChatIdentity();

    // Clear all cached data
    queryClient.clear();
    
    // Clear all localStorage and sessionStorage
    localStorage.clear();
    sessionStorage.clear();
    
    // Redirect to logout endpoint which handles full session cleanup
    window.location.href = '/logout';
  };

  // If there's an auth error, redirect to login (only if not already on auth pages)
  useEffect(() => {
    if (error && hasToken) {
      // Invalid token - remove it
      localStorage.removeItem('token');
      localStorage.removeItem('token_timestamp');
      
      // Only redirect if not already on auth pages to prevent loops
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/auth/')) {
        window.location.href = '/auth/login';
      }
    }
  }, [error, hasToken]);

  // Identify user in analytics and Sentry when auth data loads (once per user)
  const identifiedUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (user && authResponse?.organization && identifiedUserRef.current !== user.id) {
      identifiedUserRef.current = user.id;
      analytics.identify({
        userId: user.id,
        email: user.email,
        organizationId: authResponse.organization.id,
        organizationName: authResponse.organization.name,
        plan: authResponse.organization.planType,
        planTier: authResponse.organization.planType as any,
        subscriptionStatus: authResponse.organization.lifecycle as any,
        role: authResponse.organization.role,
      });

      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      });
      Sentry.setTag("organization_id", authResponse.organization.id);
      Sentry.setTag("organization_name", authResponse.organization.name);
      Sentry.setTag("plan", authResponse.organization.planType);
      Sentry.setTag("user_role", authResponse.organization.role);
    }
  }, [user?.id, authResponse?.organization?.id]);

  return {
    user: user as User | undefined,
    isLoading: isLoading && hasToken, // Only show loading if we have a token
    isAuthenticated: !!user && hasToken,
    logout,
  };
}
